import React, { useState, useEffect, useCallback } from 'react';
import type { FC, PropsWithChildren } from 'react';
import { ProgressForm } from '../../../components';
import { Modal, ModalForm, InputsSectionColumn } from '../../../components/Modal';
import ModalHeading from '../../../components/Modal/ModalHeading';

import Step1, { Istep1States } from './Step1';
import Step2, { IImageNameList } from './Step2';
import {
  usePainLocations,
  useActiveClinics,
  useAddCampaign,
  useDeleteCampaignImage,
  useGetCampaignImageList,
  useUploadPresInfo
} from '../../../hooks/advertisements';
import {
  IAd,
  IAdList,
  IImageSrcList,
  IUploadPresInfo
} from '../../../hooks/advertisements/index.type';
import Step3 from './Step3';
import Step4 from './Step4';

export type CreateCampaignModalState = PropsWithChildren<{
  status: boolean;
  data?: IAd;
}>;

export interface IPresInfoData {
  uploadDoc?: IUploadPresInfo;
  prescriptionInfoFile?: string;
  prescriptionInfoId?: string;
  prescriptionInfoFileName?: string;
  image: string;
  data?: File;
  value: string;
  error: string;
}

interface VolumeLimit {
  image: string;
  isPaid: boolean;
  min: { value: number; error: string };
  max: { value: number; error: string };
}

interface DateRangeData {
  image: string;
  from: { date: string; error: string };
  to: { date: string; error: string };
}

type CreateCampaignModalProps = PropsWithChildren<{
  getAllAds: () => Promise<void>;
  createCampaignModal: CreateCampaignModalState;
  setCreateCampaignModal: React.Dispatch<React.SetStateAction<CreateCampaignModalState>>;
  onCloseModal: () => void;
}>;

const CreateCampaignModal: FC<CreateCampaignModalProps> = ({
  getAllAds,
  createCampaignModal,
  setCreateCampaignModal,
  onCloseModal
}) => {
  // Campaign state
  const [isNewCampaign, setIsNewCampaign] = useState<IAd['advertise']>();
  const [formError, setFormError] = useState<string>('');
  const [step, setStep] = useState<number>(1);

  // Step 1 states
  const initialStep1States: Istep1States = {
    title: { value: '', error: '' },
    subTitle: { value: '', error: '' },
    description: { value: '', error: '' },
  };
  const [step1States, setStep1States] = useState<Istep1States>(initialStep1States);

  // Image related states
  const [imageDimensionError, setImageDimensionError] = useState<boolean>(false);
  const [maxReached, setMaxReached] = useState<{ status: boolean; data?: IAdList }>({ status: false });
  const [imageList, setImageList] = useState<File[]>([]);
  const [imageDeleteId, setImageDeleteId] = useState<{ item?: { img: IImageSrcList }; index?: number }>({});
  const [imageNameList, setImageNameList] = useState<IImageNameList[]>([]);
  const [imageSrcList, setImageSrcList] = useState<IImageSrcList[]>([]);
  const [imageSrcListTemp, setImageSrcListTemp] = useState<IImageSrcList[]>([]);
  const [newImgSrcList, setNewImgSrcList] = useState<{ img: IImageSrcList }[]>([]);

  // Rules and validity states
  const [ruleList, setRuleList] = useState<IAd['advertise']['ruleList']>([]);
  const [hasTimeValidity, setHasTimeValidity] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeData[]>([]);

  // Prescription info states
  const [hasPresInfo, setHasPresInfo] = useState<string[]>([]);
  const [prescriptionInfoData, setPrescriptionInfoData] = useState<IPresInfoData[]>([]);

  // Volume limit states
  const [hasVolumeLimit, setHasVolumeLimit] = useState<string[]>([]);
  const [volumeLimitData, setVolumeLimitData] = useState<VolumeLimit[]>([]);

  const [submitBtnDisabled, setSubmitBtnDisabled] = useState(false);

  // Hooks
  const { activeClinicsData } = useActiveClinics();
  const { painLocationsData } = usePainLocations();
  const { addCampaign, addCampaignLoading } = useAddCampaign();
  const { uploadPresInfo, uploadPresInfoLoading } = useUploadPresInfo();
  const { deleteImageLoading, deleteImage } = useDeleteCampaignImage({
    imageId: imageDeleteId.item?.img._id
  });
  const { getCampaignImage, getCampaignImageLoading } = useGetCampaignImageList({
    campaignId: isNewCampaign?._id || createCampaignModal.data?._id || '',
  });

  // Initialize data from existing campaign
  useEffect(() => {
    const campaignData = createCampaignModal.data;
    if (!campaignData?.campaignList?.length) return;

    // Set prescription info for existing campaigns
    const presInfoIds = campaignData.campaignList
      .filter(item => item.prescriptionInfoId)
      .map(item => item._id);
    setHasPresInfo(presInfoIds);

    // Set rule list
    if (campaignData.advertise?.ruleList) {
      setRuleList(campaignData.advertise.ruleList);
    }
  }, [createCampaignModal]);

  // Initialize step 1 data and fetch images for existing campaigns
  useEffect(() => {
    const advertiseData = createCampaignModal.data?.advertise;
    if (!advertiseData) return;

    setRuleList(advertiseData.ruleList || []);
    setStep1States({
      title: { value: advertiseData.title || '', error: '' },
      subTitle: { value: advertiseData.subTitle || '', error: '' },
      description: { value: advertiseData.description || '', error: '' },
    });

    getImageList();
  }, [createCampaignModal.data]);

  // Update data arrays when image list changes
  useEffect(() => {
    if (!imageSrcList.length) return;

    const rangeData: DateRangeData[] = imageSrcList.map(imageData => ({
      image: imageData._id,
      from: { date: '', error: '' },
      to: { date: '', error: '' },
    }));

    const presData: IPresInfoData[] = imageSrcList.map(imageData => ({
      image: imageData._id,
      value: '',
      error: '',
    }));

    const volumeData: VolumeLimit[] = imageSrcList.map(imageData => ({
      image: imageData._id,
      isPaid: imageData.isPaid || false,
      min: { value: 0, error: '' },
      max: { value: 0, error: '' },
    }));

    setDateRange(rangeData);
    setPrescriptionInfoData(presData);
    setVolumeLimitData(volumeData);
  }, [imageSrcList]);

  // Validate prescription info data
  useEffect(() => {
    const hasErrors = prescriptionInfoData.some(data => data.error !== '');
    setSubmitBtnDisabled(hasErrors);
  }, [prescriptionInfoData, hasPresInfo]);

  // Fetch image list on mount
  useEffect(() => {
    getImageList();
  }, []);

  const getImageList = useCallback(async () => {
    const campaignId = isNewCampaign?._id || createCampaignModal.data?._id;
    if (!campaignId) return;

    try {
      const data = await getCampaignImage();
      if (!data?.status || !data.data.length) return;

      const result = data.data;
      const tempArray = result.map(item => ({
        ...item,
        _id: item._id,
        label: item.imageName,
        value: item._id,
        imageUrl: item.imageUrl,
        selected: false,
        prescriptionInfoId: item.prescriptionInfoId,
        prescriptionInfoFile: item.prescriptionInfoFile,
        hasVolumeLimit: item.hasVolumeLimit,
        hasDateRange: item.hasDateRange,
        from: item.from,
        to: item.to,
        isPaid: item.isPaid,
        min: { value: item.hasVolumeLimit ? item.minVolume : 0 },
        max: { value: item.hasVolumeLimit ? item.maxVolume : 0 },
      }));

      // Set volume limit data
      const volData = result.map(item => ({
        image: item._id,
        isPaid: item.isPaid || false,
        min: { value: item.hasVolumeLimit ? item.minVolume : 0, error: '' },
        max: { value: item.hasVolumeLimit ? item.maxVolume : 0, error: '' },
      }));
      setVolumeLimitData(volData);
      setHasVolumeLimit(result.filter(item => item.hasVolumeLimit).map(item => item._id));

      // Set date range data
      const dateRangeData = result.map(item => ({
        image: item._id,
        from: { date: item.hasDateRange ? item.from : '', error: '' },
        to: { date: item.hasDateRange ? item.to : '', error: '' },
      }));
      setDateRange(dateRangeData);
      setHasTimeValidity(result.filter(item => item.hasDateRange).map(item => item._id));

      setImageSrcList(tempArray);
      setImageSrcListTemp(tempArray);

      const newImgList = tempArray.map(item => ({ img: item }));
      setNewImgSrcList(newImgList);

      // Update ruleList with complete latest data from Step 3 to sync with Step 4
      // Update ruleList with complete latest data from Step 3 to sync with Step 4
      setRuleList(prevRuleList =>
        prevRuleList.map(rule => ({
          ...rule,
          adsList: rule.adsList.map(ad => {
            const updatedImage = tempArray.find(img => img._id === ad._id);
            if (updatedImage) {
              return {
                ...ad,
                // Volume limit properties
                hasVolumeLimit: updatedImage.hasVolumeLimit,
                max: updatedImage.hasVolumeLimit ? updatedImage.maxVolume : 0,
                min: updatedImage.hasVolumeLimit ? updatedImage.minVolume : 0,
                maxVolume: updatedImage.hasVolumeLimit ? updatedImage.maxVolume : 0,
                minVolume: updatedImage.hasVolumeLimit ? updatedImage.minVolume : 0,
                // Date range properties
                hasDateRange: updatedImage.hasDateRange,
                from: updatedImage.hasDateRange ? updatedImage.from : '',
                to: updatedImage.hasDateRange ? updatedImage.to : '',
                // Prescription info properties
                prescriptionInfoId: updatedImage.prescriptionInfoId || '',
                prescriptionInfoFile: updatedImage.prescriptionInfoFile || '',
                // Payment status
                isPaid: updatedImage.isPaid,
                // Image properties
                imageName: updatedImage.imageName,
                imageUrl: updatedImage.imageUrl,
                imageType: updatedImage.imageType,
                // Update properties that exist on both
                selected: updatedImage.selected,
                label: updatedImage.label,
                value: updatedImage.value,
              };
            }
            return ad;
          })
        }))
      );
    } catch (error) {
      console.error('Error fetching image list:', error);
      setFormError('Failed to fetch image list');
    }
  }, [isNewCampaign?._id, createCampaignModal.data?._id, getCampaignImage]);

  const onFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormError('');
    if (!e.target.files) return;

    const files = Array.from(e.target.files);
    setImageList(prev => [...prev, ...files]);

    const newImageData: IImageNameList[] = [];

    files.forEach(file => {
      if (!file.type.match('image')) return;

      const picReader = new FileReader();
      picReader.addEventListener('load', (event) => {
        const picFile = event.target;
        if (!picFile?.result) return;

        const imageData: IImageNameList = {
          img: picFile.result,
          value: 0,
          isAltered: false,
        };
        newImageData.push(imageData);

        if (newImageData.length === files.filter(f => f.type.match('image')).length) {
          setImageNameList(prev => [...prev, ...newImageData]);
        }
      });

      picReader.readAsDataURL(file);
    });
  }, [imageNameList]);

  const removeImage = useCallback((index: number) => {
    setImageNameList(prev => prev.filter((_, i) => i !== index));
    setImageList(prev => prev.filter((_, i) => i !== index));
  }, []);

  const uploadPrescriptionInfo = useCallback(async () => {
    debugger;
    if (!prescriptionInfoData.length || !hasPresInfo.length) {
      handleCreateCampaign();
      return;
    }

    const hasFileToUpload = prescriptionInfoData.filter(item => item.data);
    if (!hasFileToUpload.length) {
      handleCreateCampaign();
      return;
    }

    try {
      const formData = new FormData();
      prescriptionInfoData.forEach(prescriptionInfo => {
        if (prescriptionInfo.data) {
          formData.append('doc', prescriptionInfo.data);
        }
      });

      const data = await uploadPresInfo(formData);
      if (!data?.status) {
        setFormError('Failed to upload prescription info');
        return;
      }

      const responseData = data.data;
      const updatedPrescriptionData = prescriptionInfoData.map(item => {
        const uploadedItem = responseData.find((_, index) =>
          prescriptionInfoData.filter(p => p.data)[index]?.image === item.image
        );

        if (uploadedItem && item.data) {
          return {
            ...item,
            uploadDoc: uploadedItem,
            prescriptionInfoFile: uploadedItem.docUrl,
            prescriptionInfoId: uploadedItem._id,
            prescriptionInfoFileName: uploadedItem.docName,
          };
        }
        return item;
      });

      setPrescriptionInfoData(updatedPrescriptionData);
      handleCreateCampaign('uploadPrescription', updatedPrescriptionData);
    } catch (error) {
      console.error('Error uploading prescription info:', error);
      setFormError('Failed to upload prescription info');
    }
  }, [prescriptionInfoData, hasPresInfo, uploadPresInfo]);

  const handleCreateCampaign = useCallback(async (
    type?: string,
    prescriptionData?: IPresInfoData[]
  ) => {
    // Validation for step 1
    if (step === 1 && !step1States.title.value.trim()) {
      setStep1States(prev => ({
        ...prev,
        title: { ...prev.title, error: 'Please enter campaign title' }
      }));
      return;
    }

    // Validation for step 2
    if (step === 2 && !createCampaignModal.data && imageList.length === 0) {
      setFormError('Please upload at least one campaign image');
      return;
    }

    try {
      const formData = new FormData();

      // Add campaign ID if editing
      const campaignId = createCampaignModal.data?._id || isNewCampaign?._id;
      if (campaignId) {
        formData.append('_id', campaignId);
      }

      // Add basic campaign info
      formData.append('title', step1States.title.value);
      formData.append('subTitle', step1States.subTitle.value);
      formData.append('description', step1States.description.value);

      // Add rule list for step 4 or existing campaigns
      if ((ruleList.length && step === 4) || createCampaignModal.data?._id) {
        const processedRuleList = ruleList.map(list => ({
          ...list,
          adsList: list.adsList?.map(ad => ({
            ...ad,
            customPriority: false,
            maxVolume: typeof ad.max === 'object' ? Number(ad.max.value) : ad.max,
            minVolume: typeof ad.min === 'object' ? Number(ad.min.value) : ad.min,
            max: typeof ad.max === 'object' ? Number(ad.max.value) : ad.max,
            min: typeof ad.min === 'object' ? Number(ad.min.value) : ad.min,
          })) || []
        }));
        formData.append('ruleList', JSON.stringify(processedRuleList));
      }

      // Add date range data for step 3
      if (step === 3 && imageSrcList.length > 0) {
        const dateRangeUpdated = imageSrcList.map(item => ({
          image: item._id,
          hasDateRange: hasTimeValidity.includes(item._id),
          from: { date: hasTimeValidity.includes(item._id) ? item.from : '' },
          to: { date: hasTimeValidity.includes(item._id) ? item.to : '' },
        }));
        formData.append('dateRange', JSON.stringify(dateRangeUpdated));
      }

      // Add volume limit data
      if (imageSrcList.length > 0) {
        const volumeLimit = imageSrcList.map(item => ({
          image: item._id,
          isPaid: item.isPaid,
          hasVolumeLimit: item.hasVolumeLimit,
          min: String(item.min?.value || 0),
          max: String(item.max?.value || 0),
        }));
        if (step === 3) {
          formData.append('volumeLimiter', JSON.stringify(volumeLimit));
        }
      }

      // Add prescription info data
      if (type === 'uploadPrescription' && prescriptionData) {
        const prescriptionInfoDataUpdated = prescriptionData
          .filter(item => hasPresInfo.includes(item.image))
          .map(item => {
            if (item.uploadDoc && Object.keys(item.uploadDoc).length) {
              return {
                ...item,
                prescriptionInfoFile: item.uploadDoc.docUrl,
                prescriptionInfoId: item.uploadDoc._id,
              };
            }
            return item;
          });
        formData.append('prescriptionInfoData', JSON.stringify(prescriptionInfoDataUpdated));
      } 
      
      else if (step === 3 && imageSrcList.length > 0) {
        const prescriptionInfoDataUpdated = imageSrcList.map(item => {
          const hasPresInfoEnabled = hasPresInfo.includes(item._id);

          return {
            image: item._id,
            uploadDoc: {
              docUrl: hasPresInfoEnabled ? (item?.prescriptionInfoFile || null) : null,
              _id: hasPresInfoEnabled ? (item?.prescriptionInfoId || null) : null
            }
          };
        });

        // if(hasPresInfoEnabled)

        formData.append('prescriptionInfoData', JSON.stringify(prescriptionInfoDataUpdated));
      }

      // Add image files
      imageList.forEach(img => {
        formData.append('images', img, img.name);
      });

      const data = await addCampaign(formData);
      if (!data?.status) {
        setFormError('Failed to create/update campaign');
        return;
      }

      // Handle success
      if (!createCampaignModal.data?._id) {
        setIsNewCampaign(data.result);
      }

      if (step < 4) {
        setStep(prev => prev + 1);
        setImageList([]);
        // Refresh image list after step 2 and step 3 to get updated data
        if (step === 2 || step === 3) {
          await getImageList();
        }
      } else {
        onCloseModal();
        await getAllAds();
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      setFormError('Failed to create/update campaign');
    }
  }, [
    step,
    step1States,
    createCampaignModal.data,
    imageList,
    isNewCampaign,
    ruleList,
    imageSrcList,
    hasTimeValidity,
    hasVolumeLimit,
    hasPresInfo,
    addCampaign,
    onCloseModal,
    getAllAds,
    getImageList
  ]);

  // Handle image deletion
  useEffect(() => {
    if (imageDeleteId.item && imageDeleteId.index !== undefined) {
      onImageDelete(imageDeleteId.item, imageDeleteId.index);
    }
  }, [imageDeleteId]);

  const onImageDelete = useCallback(async (
    item: { img: IImageSrcList },
    index: number
  ) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      setImageDeleteId({});
      return;
    }

    try {
      const data = await deleteImage();
      if (!data?.status) {
        setFormError('Failed to delete image');
        setImageDeleteId({});
        return;
      }

      // Remove from new image list
      setNewImgSrcList(prev => prev.filter((_, i) => i !== index));

      // Remove from rule list
      setRuleList(prev => prev.map(ruleItem => ({
        ...ruleItem,
        adsList: ruleItem.adsList.filter(ad => ad._id !== item.img._id)
      })));

      setImageDeleteId({});
    } catch (error) {
      console.error('Error deleting image:', error);
      setFormError('Failed to delete image');
      setImageDeleteId({});
    }
  }, [deleteImage]);

  const prevStep = useCallback(() => {
    setStep(prev => prev - 1);
  }, []);

  const multiForm = () => {
    switch (step) {
      case 1:
        return (
          <Step1
            loading={addCampaignLoading}
            step1StatesStates={step1States}
            setStep1StatesStates={setStep1States}
            onCancel={onCloseModal}
          />
        );
      case 2:
        return (
          <Step2
            loading={addCampaignLoading || deleteImageLoading || getCampaignImageLoading}
            imageDimensionError={imageDimensionError}
            setImageDimensionError={setImageDimensionError}
            onFileSelected={onFileSelected}
            removeImage={removeImage}
            setImageDeleteId={setImageDeleteId}
            imageNameList={imageNameList}
            newImgSrcList={newImgSrcList}
            formError={formError}
            prevStep={prevStep}
            onCancel={onCloseModal}
          />
        );
      case 3:
        return (
          <Step3
            loading={uploadPresInfoLoading || addCampaignLoading}
            imageSrcList={imageSrcList}
            setImageSrcList={setImageSrcList}
            hasTimeValidity={hasTimeValidity}
            setHasTimeValidity={setHasTimeValidity}
            hasPresInfo={hasPresInfo}
            sethasPresInfo={setHasPresInfo}
            prescriptionInfoData={prescriptionInfoData}
            setPrescriptionInfoData={setPrescriptionInfoData}
            hasVolumeLimit={hasVolumeLimit}
            sethasVolumeLimit={setHasVolumeLimit}
            volumeLimitData={volumeLimitData}
            setVolumeLimitData={setVolumeLimitData}
            dateRange={dateRange}
            setDateRange={setDateRange}
            prevStep={prevStep}
            formError={formError}
            submitBtnDisabled={submitBtnDisabled}
            onCancel={onCloseModal}
          />
        );
      case 4:
        return (
          <Step4
            loading={addCampaignLoading}
            imageSrcListTemp={imageSrcListTemp}
            ruleList={ruleList}
            setRuleList={setRuleList}
            maxReached={maxReached}
            setMaxReached={setMaxReached}
            formError={formError}
            setFormError={setFormError}
            imageSrcList={imageSrcList}
            activeClinic={activeClinicsData}
            painLocations={painLocationsData}
            prevStep={prevStep}
            onCancel={onCloseModal}
          />
        );
      default:
        return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 3) {
      uploadPrescriptionInfo();
    } else {
      handleCreateCampaign();
    }
  };

  return (
    <Modal show={createCampaignModal.status} style={{ minWidth: '70vw', minHeight: '90vh' }}>
      <ModalHeading
        heading={createCampaignModal.data?.advertise._id ? 'Edit Campaign' : 'Create New Campaign'}
      />
      <ModalForm
        style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}
        onSubmit={handleSubmit}
      >
        <div className='flex'>
          <InputsSectionColumn style={{ width: '100%', padding: '1rem' }}>
            <ProgressForm step={step} />
          </InputsSectionColumn>
        </div>
        {multiForm()}
      </ModalForm>
    </Modal>
  );
};

export default CreateCampaignModal;